import { useEffect, useRef } from "react";
import styles from "./Services.module.css";

const services = [
  {
    icon: "📱",
    title: "React Native Apps",
    desc: "Cross-platform iOS & Android apps built with React Native — one codebase, two platforms, native performance.",
    tags: ["iOS", "Android", "React Native"],
    color: "#63d2be",
  },
  {
    icon: "🌐",
    title: "Web Platforms",
    desc: "Scalable web applications, admin dashboards, and customer portals built with modern React and TypeScript.",
    tags: ["React", "TypeScript", "Node.js"],
    color: "#f0a05a",
  },
  {
    icon: "⚙️",
    title: "Custom Software",
    desc: "Bespoke backend systems, APIs, and automation tools tailored precisely to your business workflow.",
    tags: ["API Design", "Backend", "Automation"],
    color: "#a78bfa",
  },
  {
    icon: "✦",
    title: "UX & Product Design",
    desc: "From wireframes to pixel-perfect UI — we design interfaces that feel effortless and convert.",
    tags: ["UX", "UI", "Prototyping"],
    color: "#f472b6",
  },
] as const;

export default function Services() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.15 }
    );
    const els = sectionRef.current?.querySelectorAll(".reveal");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" ref={sectionRef} className={styles.section}>
      <div className={styles.header}>
        <p className={`${styles.eyebrow} reveal`}>// What We Do</p>
        <h2 className={`${styles.heading} reveal`}>
          Full-stack studio,{" "}
          <span className={styles.accent}>end-to-end.</span>
        </h2>
        <p className={`${styles.sub} reveal`}>
          From product strategy through to App Store submission — we handle the
          full build so you can focus on your business.
        </p>
      </div>

      <div className={styles.grid}>
        {services.map((s, i) => (
          <article
            key={s.title}
            className={`${styles.card} reveal`}
            style={{ transitionDelay: `${i * 0.08}s` }}
          >
            <div className={styles.iconWrap} style={{ color: s.color }}>
              <span className={styles.icon}>{s.icon}</span>
              <div
                className={styles.iconGlow}
                style={{ background: `${s.color}1a` }}
              />
            </div>
            <h3 className={styles.cardTitle}>{s.title}</h3>
            <p className={styles.cardDesc}>{s.desc}</p>
            <div className={styles.tags}>
              {s.tags.map((t) => (
                <span
                  key={t}
                  className={styles.tag}
                  style={{
                    color: s.color,
                    borderColor: `${s.color}33`,
                    background: `${s.color}0d`,
                  }}
                >
                  {t}
                </span>
              ))}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
